import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRightLong, faEnvelope, faPhone,
  faMapMarkerAlt, faClock, faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { API_BASE_URL } from "../../api";
import "./ContactSection.scss";

const infos = [
  { icon: faEnvelope,     key: "email",   value: "contact@saydalani.com" },
  { icon: faPhone,        key: "phone",   value: "+212 6 09 89 92 72" },
  { icon: faMapMarkerAlt, key: "address", value: null },
  { icon: faClock,        key: "support", value: null },
];

export default function ContactSection() {
  const { t } = useTranslation();
  const [form, setForm]           = useState({ name: "", email: "", phone: "", pharmacy: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]     = useState(false);

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    setSending(true);
    axios.post(`${API_BASE_URL}/api/v1/contact`, form)
      .then(() => setSubmitted(true))
      .catch(() => toast.error(t("landing.contact.error")))
      .finally(() => setSending(false));
  };

  return (
    <section id="contact" className="syd-contact">
      <div className="container">

        {/* Header */}
        <motion.div
          className="syd-contact__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="syd-contact__label">{t("landing.nav.contact")}</div>
          <h2 className="syd-contact__title">{t("landing.contact.title")}</h2>
          <p className="syd-contact__sub">
            {t("landing.contact.sub")}
          </p>
        </motion.div>

        <div className="syd-contact__inner">

          {/* ── Left — info ── */}
          <motion.div
            className="syd-contact__info"
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65 }}
            viewport={{ once: true }}
          >
            <div className="syd-contact__info-cards">
              {infos.map((info, i) => (
                <div className="syd-contact__info-card" key={i}>
                  <div className="syd-contact__info-icon">
                    <FontAwesomeIcon icon={info.icon} />
                  </div>
                  <div>
                    <div className="syd-contact__info-label">{t(`landing.contact.info_${info.key}_label`)}</div>
                    <div className="syd-contact__info-value">{info.value ?? t(`landing.contact.info_${info.key}_value`)}</div>
                    <div className="syd-contact__info-sub">{t(`landing.contact.info_${info.key}_sub`)}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="syd-contact__promise">
              <div className="syd-contact__promise-icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <div>
                <div className="syd-contact__promise-title">{t("landing.contact.promise_title")}</div>
                <div className="syd-contact__promise-desc">
                  {t("landing.contact.promise_desc")}
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── Right — form ── */}
          <motion.div
            className="syd-contact__form-wrap"
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65 }}
            viewport={{ once: true }}
          >
            {submitted ? (
              <div className="syd-contact__sent">
                <div className="syd-contact__sent-icon">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </div>
                <h3>{t("landing.contact.sent_title")}</h3>
                <p>{t("landing.contact.sent_desc")}</p>
              </div>
            ) : (
              <form className="syd-contact__form" onSubmit={submit}>
                <div className="syd-contact__form-title">{t("landing.contact.form_title")}</div>

                <div className="syd-contact__row">
                  <div className="syd-contact__field">
                    <label htmlFor="c-name">{t("landing.contact.field_name")}</label>
                    <input id="c-name" name="name" type="text" placeholder={t("landing.contact.field_name_placeholder")} value={form.name} onChange={handle} required />
                  </div>
                  <div className="syd-contact__field">
                    <label htmlFor="c-email">{t("Email")}</label>
                    <input id="c-email" name="email" type="email" placeholder={t("landing.contact.field_email_placeholder")} value={form.email} onChange={handle} required />
                  </div>
                </div>

                <div className="syd-contact__row">
                  <div className="syd-contact__field">
                    <label htmlFor="c-phone">{t("landing.contact.field_phone")}</label>
                    <input id="c-phone" name="phone" type="tel" placeholder="+212 6 00 00 00 00" value={form.phone} onChange={handle} />
                  </div>
                  <div className="syd-contact__field">
                    <label htmlFor="c-pharmacy">{t("landing.contact.field_pharmacy")}</label>
                    <input id="c-pharmacy" name="pharmacy" type="text" placeholder={t("landing.contact.field_pharmacy_placeholder")} value={form.pharmacy} onChange={handle} />
                  </div>
                </div>

                <div className="syd-contact__field">
                  <label htmlFor="c-message">{t("landing.contact.field_message")}</label>
                  <textarea
                    id="c-message"
                    name="message"
                    placeholder={t("landing.contact.field_message_placeholder")}
                    rows={4}
                    value={form.message}
                    onChange={handle}
                    required
                  />
                </div>

                <button type="submit" className="syd-contact__submit" disabled={sending}>
                  {sending ? t("landing.contact.sending") : t("landing.contact.submit")}
                  <FontAwesomeIcon icon={faArrowRightLong} />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
